import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dumbbell, Activity, Zap, Stethoscope, Clock, Check, X } from "lucide-react";

import { BACKEND_URL as API } from "@/lib/api";
const HTTP = axios.create({ baseURL: `${API}/api`, withCredentials: true });

const TYPE_META = {
  strength:          { label: "Strength training",  icon: Dumbbell,    color: "text-amber-300" },
  conditioning:      { label: "Conditioning",       icon: Activity,    color: "text-cyan-300" },
  speed_agility:     { label: "Speed / Agility",    icon: Zap,         color: "text-violet-300" },
  athletic_training: { label: "Athletic Training",  icon: Stethoscope, color: "text-emerald-300" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function PlayerWorkloadCard() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("strength");
  const [duration, setDuration] = useState(45);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await HTTP.get("/workload/sessions?days=14");
      setItems(r.data?.sessions || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await HTTP.post("/workload/sessions/self", {
        session_type: type,
        duration_min: Number(duration) || 0,
        notes: notes || null,
      });
      toast.success("Attendance logged — staff will verify");
      setNotes("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log");
    } finally { setBusy(false); }
  };

  const counts = items.reduce((m, s) => { m[s.status] = (m[s.status] || 0) + 1; return m; }, {});

  return (
    <Card data-testid="player-workload-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Dumbbell className="h-4 w-4" /> Log Attendance
        </CardTitle>
        <CardDescription>
          Check in at S&amp;C, Conditioning, Speed/Agility, or the Athletic-Training room.
          Staff verify and it feeds your Readiness Score.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">What did you do?</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="player-workload-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Duration (min)</Label>
            <Input type="number" min={0} max={300} value={duration}
              onChange={(e) => setDuration(e.target.value)} data-testid="player-workload-duration" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Notes (optional)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="What was the focus?" data-testid="player-workload-notes" />
        </div>
        <Button onClick={submit} disabled={busy} className="w-full" data-testid="player-workload-submit">
          Log attendance
        </Button>

        {!!items.length && (
          <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/60 space-y-2">
            <div className="flex gap-2">
              {counts.verified ? <Badge>{counts.verified} verified</Badge> : null}
              {counts.pending ? <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />{counts.pending} pending</Badge> : null}
              {counts.rejected ? <Badge variant="destructive">{counts.rejected} rejected</Badge> : null}
            </div>
            <div className="space-y-1 max-h-40 overflow-auto">
              {items.slice(0, 8).map((s) => {
                const M = TYPE_META[s.session_type];
                const I = M?.icon;
                return (
                  <div key={s.session_id} className="flex items-center justify-between rounded border border-white/5 px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      {I && <I className={`h-3.5 w-3.5 ${M.color}`} />}
                      <span>{M?.label || s.session_type}</span>
                      <span className="text-white/40">· {s.duration_min}m</span>
                      {s.status === "verified" && <Check className="h-3 w-3 text-emerald-400" />}
                      {s.status === "rejected" && <X className="h-3 w-3 text-rose-400" />}
                      {s.status === "pending" && <Clock className="h-3 w-3 text-amber-300" />}
                    </div>
                    <span className="text-white/40">{fmt(s.date)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
